/**
 * Las medidas de los cinco formatos de rotulación, en milímetros. **El único
 * sitio que las fija.** La del PAPEL y cuánto cabe en él son de todo lo que se
 * imprime y viven en `componentes/impresos/hoja.ts`.
 *
 * ⚠ **Existe porque el .xls no tiene medidas, tiene columnas.** En
 * `Etiquetas, caratulas, etc de carpetas.xls` una etiqueta es un bloque de celdas
 * fusionadas: mide lo que midan las columnas de esa copia del archivo. Basta con que
 * alguien ensanche una columna para ajustar un texto —o con abrirlo en un equipo con
 * otra fuente por defecto— para que la etiqueta salga de otro tamaño y deje de
 * cuadrar con la carpeta. Eso es la «deformación»: no es un fallo de Excel, es que
 * ahí la medida del papel es una consecuencia y no un dato.
 *
 * Aquí es un dato. Un milímetro es un milímetro en pantalla, en la vista previa y en
 * el papel, porque el CSS los declara en `mm` y el navegador los convierte usando la
 * resolución real de la impresora.
 *
 * ⚠ **Nada de esto se guarda en la fila del rótulo.** Ver la nota de
 * `models/rotulacion.py`: si la medida viajara en los datos, corregir un ancho
 * obligaría a reescribir todo lo ya impreso. La fila dice QUÉ pone; este archivo dice
 * CUÁNTO mide.
 */

export type Medida = {
  /** Ancho del rótulo terminado, en mm. */
  ancho: number
  /** Alto del rótulo terminado, en mm. */
  alto: number
  /** Cuántos caben a lo ancho de la hoja. */
  columnas: number
  /** Nombre legible para la pantalla. */
  etiqueta: string
  /** De dónde sale la medida, para que nadie la cambie a ojo. */
  procedencia: string
}

/**
 * ⚠ **Las medidas salen del .xls, redondeadas al milímetro y luego cuadradas a la
 * carpeta real.** El Excel da 19,80 cm de ancho para la etiqueta de carpeta; aquí
 * son 190 mm y no 198, porque la tapa de una carpeta carta mide 220 mm y una
 * etiqueta de 198 deja 11 mm por lado — que es menos de lo que se despega al abrirla
 * y cerrarla cien veces. 190 deja 15 y se pega dentro del área plana.
 */
export const MEDIDAS = {
  PESTANA: {
    ancho: 90,
    alto: 16,
    columnas: 2,
    etiqueta: "Pestaña",
    procedencia: "Manual de identidad: 90 × 16 mm, en pliego para recortar",
  },
  ETIQUETA_CARPETA: {
    ancho: 190,
    alto: 128,
    columnas: 1,
    etiqueta: "Etiqueta de tapa",
    procedencia: "xls «ETIQ CARP» 19,80 cm, ajustada a la tapa carta (220 mm)",
  },
  /**
   * ⚠ **El lomo es el único que NO tiene ancho fijo aquí**: lo pone el rótulo, en
   * `datos.ancho_mm`, porque depende de la carpeta física (55, 75, 85 mm en el mismo
   * estante). Este `ancho` es sólo el valor por defecto de la vista previa.
   *
   * ⚠ **El `alto` es el largo de la tira, y son 180 mm y NO los 285 del canto de una
   * carpeta carta.** El primer intento fue 285 —cubrir el canto entero— y no cabe en
   * una hoja carta por ninguna de las dos orientaciones: 259 mm de alto útil, 196 de
   * ancho. Habría salido recortada, y en un módulo que existe para que la medida
   * cuadre eso es el peor fallo posible. 180 mm cabe DE PIE con holgura —que es como
   * se dibuja e imprime, ver `LomoCarpeta`— y es más del doble de la tira de 84 mm
   * que usa hoy el `.xls`: una etiqueta de lomo se pega en la franja de lectura, no
   * forra el canto de arriba abajo.
   *
   * Para cubrirlo entero hace falta papel oficio (216×356 mm). Si algún día se
   * quiere, se añade un formato aparte con su `@page`; no se sube este número.
   */
  LOMO: {
    ancho: 60,
    alto: 180,
    columnas: 1,
    etiqueta: "Lomo de carpeta",
    procedencia: "xls «LOMOS» 8,44 cm, ampliado a 180 mm; el tope es la hoja carta",
  },
  /**
   * ⚠ **La carátula ES la hoja, a sangre** (manual de identidad): se imprime con su propia
   * `@page` sin margen y por eso no entra en la cuenta del área útil. Su alto real depende
   * de la hoja elegida, `ALTO_HOJA`.
   */
  CARATULA: {
    ancho: 216,
    alto: 279,
    columnas: 1,
    etiqueta: "Carátula",
    procedencia: "Manual de identidad: hoja carta u oficio a sangre, margen interior de 19 mm",
  },
  TEJUELO: {
    ancho: 148,
    alto: 105,
    columnas: 1,
    etiqueta: "Tejuelo de libro",
    procedencia: "xls «ETIQUETAS LIBROS» 14,77 cm; A6 exacto, que es media A5",
  },
} as const satisfies Record<string, Medida>

export type ClaveFormato = keyof typeof MEDIDAS

/** La hoja de la carátula. */
export const ALTO_HOJA = { CARTA: 279, OFICIO: 356 } as const

/** Lo que admite el largo del lomo: `schemas/rotulacion.py`. */
export const LARGO_LOMO = { min: 80, max: 259 } as const

/**
 * Anchos de canto que se ofrecen en el selector del lomo.
 *
 * Son las carpetas que se compran: no es una lista cerrada —el campo admite
 * cualquier valor entre 25 y 120— sino los atajos para no medir con regla cada vez.
 */
export const ANCHOS_LOMO = [
  { mm: 40, etiqueta: "40 mm · carpeta fina" },
  { mm: 55, etiqueta: "55 mm · 2 anillos" },
  { mm: 75, etiqueta: "75 mm · palanca estándar" },
  { mm: 85, etiqueta: "85 mm · palanca ancha" },
] as const
